import "date-fns";
import React, { useState } from "react";
import {
  Formik,
  FormikActions,
  Form as FormikForm,
  Field,
  FormikProps,
} from "formik";
import { InjectedIntlProps, injectIntl, FormattedMessage } from "react-intl";
import TextInput from "../../text-input";
import Typography from "../../typography";
import InputErrorMessage from "../../input-error-message";

import messages from "./NewMatchForm.intl";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../../types/styled-arguments";
import { required } from "../../form-utils";
import { ITeam } from "../../../types";
import Select from "../../select";
import Option from "../../select/option";
import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker,
  KeyboardDatePicker,
} from "@material-ui/pickers";

import DateFnsUtils from "@date-io/date-fns";
import Button from "../../button";
import moment from "moment";
import {
  FormControlLabel,
  Switch,
  ButtonGroup,
} from "@material-ui/core";

export interface IFormValues {
  location: string;
  date: string;
  time: string;
  homeTeam: string;
  awayTeam: string;
  local: boolean;
  playersSelected: string[];
  playersConfirmed: string[];
  status: string;
}

const Title = styled(({ children }) => (
  <Typography.H5>{children}</Typography.H5>
))((props: IStyledArguments) => {
  return {
    fontWeight: 700,
    color: props.theme.palette.text.primary,
    fontSize: "2rem",
    letterSpacing: "-0.5px",
    marginBottom: props.theme.spacing(3),
    [props.theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
      marginBottom: props.theme.spacing(2),
    },
  };
});

const ImageHeader = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(0, 1, 2, 1),
  width: "100%",
  display: "flex",
  justifyContent: "center",
}));

const LogoImage = styled("img")(({ theme }: IStyledArguments) => ({
  height: "80px",
  [theme.breakpoints.down("sm")]: {
    height: "60px",
  },
}));

const TeamsSelect = injectIntl<{
  onChange: (event: any) => void;
  value: any;
  teams: ITeam[];
  intl: InjectedIntlProps;
}>(({ onChange, value, teams, intl }) => {
  if (!teams || teams.length < 1) {
    return null;
  }
  return (
    <Select
      id="team-select"
      label={intl.formatMessage(messages.homeTeam)}
      value={value}
      onChange={onChange}
      fullWidth
    >
      <Option value="">
        <em>{"Select a Team"}</em>
      </Option>
      {teams.map(({ id, name }, idx: number) => (
        <Option key={idx} value={id}>
          {name}
        </Option>
      ))}
    </Select>
  );
});

interface IForm extends InjectedIntlProps {
  onSubmit: (arg: any) => void;
  location?: string;
  date?: string;
  time?: string;
  homeTeam?: any;
  awayTeam?: string;
  teams?: any;
  local?: boolean;
  playersSelected?: any;
  playersConfirmed?: any;
  status?: string;
  isHomeTeamLocal?: boolean;
}

const StyledForm = styled(FormikForm)(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
  "& .MuiTextField-root": {
    background: theme.palette.background.paper,
    borderRadius: "10px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.primary.main,
      fontWeight: 600,
      "&.Mui-focused": {
        color: theme.palette.primary.main,
      },
    },
  },
}));

const ButtonGroupContainer = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(3, 0),
  "& .MuiTypography-subtitle1": {
    color: theme.palette.primary.main,
    fontWeight: 600,
    fontSize: "1rem",
  },
  "& .MuiButtonGroup-root": {
    borderRadius: "14px",
    overflow: "hidden",
    "& .MuiButton-root": {
      padding: "14px 40px",
      fontWeight: 700,
      fontSize: "1.05rem",
      border: "none",
      "&.MuiButton-contained": {
        background: theme.palette.primary.main,
      },
      "&.MuiButton-outlined": {
        background: theme.palette.background.paper,
        borderColor: "transparent",
      },
    },
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(2.5),
  margin: 0,
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "14px",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  "& .MuiTypography-root": {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: "1rem",
  },
  "& .MuiSwitch-colorSecondary.Mui-checked": {
    color: theme.palette.primary.main,
    "& + .MuiSwitch-track": {
      backgroundColor: theme.palette.primary.main,
    },
  },
}));

const SubmitButtonContainer = styled("div")(({ theme }: IStyledArguments) => ({
  marginTop: theme.spacing(2),
  "& button": {
    padding: "16px 48px",
    fontSize: "1.1rem",
    fontWeight: 700,
    borderRadius: "16px",
    background: theme.palette.primary.main,
    color: "#fff",
    letterSpacing: "1px",
    border: "none",
    transition: "background 0.2s ease",
    width: "100%",
    "&:hover": {
      background: theme.palette.primary.dark,
    },
    "&:disabled": {
      background: "rgba(200, 200, 200, 0.6)",
      color: "rgba(0, 0, 0, 0.3)",
    },
  },
  [theme.breakpoints.down("sm")]: {
    "& button": {
      padding: "14px 40px",
      fontSize: "1rem",
    },
  },
}));

const NewTeamForm = ({
  onSubmit,
  location = "",
  // date = "",
  // time = "",
  homeTeam = "",
  awayTeam = "",
  teams,
  isHomeTeamLocal = true,
  intl,
}: IForm) => {
  const [teamOption, setTeamOption] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<Date>(
    moment()
      .day("sunday")
      .isBefore(Date.now(), "day")
      ? moment()
          .day("sunday")
          .add(7, "day")
          .toDate()
      : moment()
          .day("sunday")
          .toDate()
  );
  const [selectedTime, setSelectedTime] = useState<Date>(
    new Date("2019-08-18T10:00:00")
  );

  const [local, setLocal] = useState<boolean>(true);
  const [friendly, setFriendly] = useState<boolean>(false);
  const [matchLocation, setMatchLocation] = useState<string>("buff");

  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };
  const handleTimeChange = (date: Date) => {
    setSelectedTime(date);
  };

  const handleLocal = () => {
    console.log("previous local================");
    console.log(local);
    console.log("new local================");
    console.log(!local);
    setLocal(!local);
  };

  const handleFriendly = (isFriendly: boolean) => {
    console.log("friendly================");
    console.log(isFriendly);
    setFriendly(isFriendly);
  };

  return (
    <Formik
      initialValues={{
        location,
        date: `${moment(selectedDate).format("YYYY-MM-DD")}`,
        time: `${moment(selectedTime).format("HH:mm")}`,
        homeTeam,
        awayTeam,
        teams,
        isHomeTeamLocal,
      }}
      onSubmit={(values: any, actions: FormikActions<any>) => {
        const toSubmit = { ...values };
        if (local && teams && teams.length === 1) {
          toSubmit.location = teams[0].location;
          toSubmit.homeTeam = teams[0].id;
        } else if (!local && teams && teams.length === 1) {
          toSubmit.homeTeam = teams[0].id;
        }
        console.log("========");
        console.log(toSubmit);
        console.log("========");
        onSubmit(toSubmit);
        actions.setSubmitting(false);
      }}
    >
      {({
        errors,
        touched,
        // values,
        isValid,
        setFieldValue,
      }: FormikProps<any>) => (
        <>
          <ImageHeader>
            <LogoImage src="/images/footik-logo.svg" alt="Footik Logo" />
          </ImageHeader>
          <Title>
            <FormattedMessage {...messages.title} />
          </Title>
            <ButtonGroupContainer>
              <Typography.Subtitle1>
                {intl.formatMessage(messages.homeAwayTitle)}
              </Typography.Subtitle1>
              <ButtonGroup
                size="large"
                color="primary"
                aria-label="large outlined primary button group"
              >
                <Button
                  color={local ? "primary" : "secondary"}
                  variant={local ? "contained" : "outlined"}
                  onClick={() => {
                    handleLocal();
                    setFieldValue("isHomeTeamLocal", !local);
                  }}
                >
                  {intl.formatMessage(messages.home)}
                </Button>
                <Button
                  color={local ? "primary" : "secondary"}
                  variant={local ? "outlined" : "contained"}
                  onClick={() => {
                    handleLocal();
                    setFieldValue("isHomeTeamLocal", !local);
                  }}
                >
                  {intl.formatMessage(messages.away)}
                </Button>
              </ButtonGroup>
            </ButtonGroupContainer>

            <StyledForm data-testid="add-match-form">
              {/* {!local && ( */}
              {/* <> */}
              <Field
                name="location"
                // validate={required(intl)}
                error={errors.location && touched.location}
                component={TextInput}
                inputProps={{
                  "data-testid": "location",
                }}
                label={
                  local
                    ? teams && teams.length === 1
                      ? teams[0].location
                      : matchLocation
                    : intl.formatMessage(messages.location)
                }
                disabled={local ? true : false}
              />
              <InputErrorMessage name="location" />
              {/* </> */}
              {/* )} */}
              {teams && teams.length === 1 ? (
                <Field
                  name="homeTeam"
                  // validate={required(intl)}
                  error={errors.location && touched.location}
                  component={TextInput}
                  inputProps={{
                    "data-testid": "location",
                  }}
                  label={teams[0].name}
                  // value={0}
                  disabled
                />
              ) : (
                <>
                  <TeamsSelect
                    teams={teams}
                    value={teamOption}
                    onChange={(event: any) => {
                      console.log("********", event.target.value);
                      console.log("********", teams);
                      const selected = teams.filter(
                        (t: any) => t.id === event.target.value
                      )[0];
                      console.log("******* selected team ", selected);
                      setMatchLocation(selected.location);
                      setTeamOption(event.target.value);
                      setFieldValue("homeTeam", event.target.value);
                    }}
                  />
                  <InputErrorMessage name="homeTeam" />
                </>
              )}
              <Field
                name="awayTeam"
                validate={required(intl)}
                error={errors.awayTeam && touched.awayTeam}
                component={TextInput}
                inputProps={{
                  "data-testid": "awayTeam",
                }}
                label={intl.formatMessage(messages.awayTeam)}
              />
              <InputErrorMessage name="awayTeam" />

              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  margin="normal"
                  id="date-picker-dialog"
                  label={intl.formatMessage(messages.date)}
                  format="yyyy-MM-dd"
                  value={selectedDate}
                  onChange={(date: any) => {
                    handleDateChange(date);
                    setFieldValue("date", moment(date).format("YYYY-MM-DD"));
                  }}
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                />
                <KeyboardTimePicker
                  margin="normal"
                  id="time-picker"
                  label={intl.formatMessage(messages.time)}
                  value={selectedTime}
                  ampm={false}
                  onChange={(time: any) => {
                    handleTimeChange(time);
                    setFieldValue("time", moment(time).format("HH:mm"));
                  }}
                  KeyboardButtonProps={{
                    "aria-label": "change time",
                  }}
                />
              </MuiPickersUtilsProvider>
              {/* <FormControlLabel
                control={
                  // <Checkbox
                  //   name="isHomeTeamLocal"
                  //   checked={local}
                  //   onChange={(_, value: any) => {
                  //     console.log("========");
                  //     console.log(value);
                  //     // const isLocal: boolean = e.target.value;
                  //     handleLocal(value);
                  //     setFieldValue("isHomeTeamLocal", value);
                  //   }}
                  //   value={local}
                  // />
                  <Switch
                    checked={local}
                    onChange={(_, value: any) => {
                      console.log("========");
                      // console.log(value);
                      // // const isLocal: boolean = e.target.value;
                      // handleLocal(value);
                      setFieldValue("isHomeTeamLocal", value);
                    }}
                    name="isHomeTeamLocal"
                    inputProps={{ "aria-label": "secondary checkbox" }}
                  />
                }
                label={intl.formatMessage(messages.isHomeTeam)}
              /> */}
              <StyledFormControlLabel
                control={
                  <Switch
                    checked={friendly}
                    onChange={(_, value: any) => {
                      console.log("========");
                      console.log(value);
                      // const isLocal: boolean = e.target.value;
                      handleFriendly(value);
                      setFieldValue("isFriendly", value);
                    }}
                    name="isHomeTeamLocal"
                    inputProps={{ "aria-label": "secondary checkbox" }}
                  />
                }
                label={intl.formatMessage(messages.friendly)}
              />
              <InputErrorMessage name="isHomeTeamLocal" />

              <SubmitButtonContainer>
                <Button
                  color="primary"
                  size="medium"
                  type="submit"
                  data-testid="go-next-button"
                  disabled={!isValid}
                >
                  {intl.formatMessage(messages.next)}
                </Button>
              </SubmitButtonContainer>
            </StyledForm>
          </>
        )}
    </Formik>
  );
};

export default injectIntl(NewTeamForm);
